import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  Button,
} from '@mui/material';
import {
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import DownloadIcon from '@mui/icons-material/Download';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import config from '../config';

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

const formatCurrency = (value) => {
  if (value >= 1000000) {
    return `$${(value / 1000000).toFixed(1)}M`;
  } else if (value >= 1000) {
    return `$${(value / 1000).toFixed(0)}K`;
  } else {
    return `$${value.toFixed(0)}`;
  }
};

const toTitleCase = (str) => {
  if (!str) return str;
  return str
    .toLowerCase()
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

const CustomTooltip = ({ active, payload }) => {
  if (active && payload && payload.length) {
    const dataPoint = payload[0].payload;
    return (
      <Box
        sx={{
          backgroundColor: '#ffffff',
          border: '1px solid #e2e8f0',
          borderRadius: '0.5rem',
          p: 1.5,
          boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)',
        }}
      >
        <Typography sx={{ fontSize: '0.813rem', fontWeight: 600, mb: 0.5, color: '#1e293b' }}>
          {dataPoint.name || dataPoint.month || dataPoint.day}
        </Typography>

        {/* Charges */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25 }}>
          <Box sx={{ width: '0.5rem', height: '0.5rem', borderRadius: '50%', bgcolor: '#2563eb' }} />
          <Typography sx={{ fontSize: '0.75rem', color: '#64748b' }}>
            Charges: <strong style={{ color: '#1e293b' }}>{formatCurrency(dataPoint.charges || 0)}</strong>
          </Typography>
        </Box>

        {/* Payments */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25 }}>
          <Box sx={{ width: '0.5rem', height: '0.5rem', borderRadius: '50%', bgcolor: '#10b981' }} />
          <Typography sx={{ fontSize: '0.75rem', color: '#64748b' }}>
            Payments: <strong style={{ color: '#1e293b' }}>{formatCurrency(dataPoint.collections || 0)}</strong>
          </Typography>
        </Box>

        {/* Adjustments - with divider above */}
        {dataPoint.adjustments !== undefined && (
          <>
            <Box sx={{ borderTop: '1px solid #e2e8f0', my: 0.75 }} />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box sx={{ width: '0.5rem', height: '0.5rem', borderRadius: '50%', bgcolor: '#f59e0b' }} />
              <Typography sx={{ fontSize: '0.75rem', color: '#64748b' }}>
                Adjustments: <strong style={{ color: '#1e293b' }}>{formatCurrency(dataPoint.adjustments || 0)}</strong>
              </Typography>
            </Box>
          </>
        )}
      </Box>
    );
  }
  return null;
};

function ChargesAndCollections({ filters, consolidateLocations = true }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [availableYears, setAvailableYears] = useState([]);
  const [year, setYear] = useState(new Date().getFullYear().toString());
  const [month, setMonth] = useState(null);
  const [location, setLocation] = useState('All');
  const [provider, setProvider] = useState('All');
  const [insurance, setInsurance] = useState('All');
  const [cpt, setCpt] = useState('All');
  const [activeDimension, setActiveDimension] = useState(null);

  const [kpis, setKpis] = useState({
    totalPatients: 0,
    totalClaims: 0,
    totalCharges: 0,
    totalCollections: 0,
  });
  const [trendData, setTrendData] = useState([]);
  const [breakdowns, setBreakdowns] = useState({
    byLocation: [],
    byPayor: [],
    byCpt: [],
    byProvider: [],
  });
  const [detailedTable, setDetailedTable] = useState([]);

  const [filterOptions, setFilterOptions] = useState({
    locations: [],
    providers: [],
    payors: [],
    cptCodes: [],
  });

  // Handler functions with mutual exclusion logic
  const handleLocationChange = (value) => {
    if (value !== 'All') {
      setProvider('All');
      setInsurance('All');
      setCpt('All');
      setActiveDimension('location');
    } else {
      setActiveDimension(null);
    }
    setLocation(value);
  };

  const handleProviderChange = (value) => {
    if (value !== 'All') {
      setLocation('All');
      setInsurance('All');
      setCpt('All');
      setActiveDimension('provider');
    } else {
      setActiveDimension(null);
    }
    setProvider(value);
  };

  const handleInsuranceChange = (value) => {
    if (value !== 'All') {
      setLocation('All');
      setProvider('All');
      setCpt('All');
      setActiveDimension('insurance');
    } else {
      setActiveDimension(null);
    }
    setInsurance(value);
  };

  const handleCptChange = (value) => {
    if (value !== 'All') {
      setLocation('All');
      setProvider('All');
      setInsurance('All');
      setActiveDimension('cpt');
    } else {
      setActiveDimension(null);
    }
    setCpt(value);
  };

  // Fetch available years on mount
  useEffect(() => {
    const fetchYears = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`${config.apiBaseUrl}/api/available-years`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        if (response.ok) {
          const data = await response.json();
          setAvailableYears(data.years || []);
          // Set default year to current year
          if (data.years && data.years.length > 0) {
            setYear(new Date().getFullYear().toString());
          }
        }
      } catch (err) {
        console.error('Error fetching available years:', err);
      }
    };

    fetchYears();
  }, []);

  // Reset location filter when consolidateLocations changes (selected value may not exist in new list)
  useEffect(() => {
    setLocation('All');
    if (activeDimension === 'location') {
      setActiveDimension(null);
    }
  }, [consolidateLocations]);

  // Fetch filter options on mount and when consolidateLocations changes
  useEffect(() => {
    const fetchFilterOptions = async () => {
      try {
        const token = localStorage.getItem('token');
        const chargesResponse = await fetch(`${config.apiBaseUrl}/api/charges-collections?year=${year}&consolidateLocations=${consolidateLocations}`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        if (chargesResponse.ok) {
          const chargesData = await chargesResponse.json();
          const locations = chargesData.breakdowns?.byLocation?.map(item => item.name) || [];
          const providers = chargesData.breakdowns?.byProvider?.map(item => item.name) || [];
          const payors = chargesData.breakdowns?.byPayor?.map(item => item.name) || [];
          const cptCodes = chargesData.breakdowns?.byCpt?.map(item => item.name) || [];

          setFilterOptions({
            locations: locations.sort(),
            providers: providers.sort(),
            payors: payors.sort(),
            cptCodes: cptCodes.sort(),
          });
        }
      } catch (err) {
        console.error('Error fetching filter options:', err);
      }
    };

    fetchFilterOptions();
  }, [year, consolidateLocations]);

  // Fetch data when filters change (with debounce)
  useEffect(() => {
    const timer = setTimeout(() => {
      const fetchData = async () => {
        setLoading(true);
        setError(null);

        try {
          const queryParams = new URLSearchParams();
          if (year) queryParams.append('year', year);
          if (month) queryParams.append('month', month);
          if (location && location !== 'All') queryParams.append('location', location);
          if (provider && provider !== 'All') queryParams.append('provider', provider);
          if (insurance && insurance !== 'All') queryParams.append('payor', insurance);
          if (cpt && cpt !== 'All') queryParams.append('cptCode', cpt);
          queryParams.append('dateType', filters?.dateType || 'post');
          queryParams.append('consolidateLocations', consolidateLocations.toString());

          const token = localStorage.getItem('token');
          const response = await fetch(`${config.apiBaseUrl}/api/charges-collections?${queryParams}`, {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          });

          if (!response.ok) {
            throw new Error(`Failed to fetch data: ${response.statusText}`);
          }

          const data = await response.json();

          setKpis(data.kpis || {});

          // Map data to match the chart structure (collections as line data)
          const mappedTrendData = (data.trendData || []).map(item => ({
            month: item.month,
            day: item.day,
            charges: item.charges,
            collections: item.collections,
            adjustments: item.adjustments,
            rate: parseFloat(item.rate)
          }));
          setTrendData(mappedTrendData);

          // Map all breakdown items (no limit)
          const mapBreakdown = (items) => items.map(item => ({
            name: item.name,
            charges: item.charges,
            collections: item.collections,
            adjustments: item.adjustments
          }));

          setBreakdowns({
            byLocation: mapBreakdown(data.breakdowns?.byLocation || []),
            byPayor: mapBreakdown(data.breakdowns?.byPayor || []),
            byCpt: mapBreakdown(data.breakdowns?.byCpt || []),
            byProvider: mapBreakdown(data.breakdowns?.byProvider || []),
          });

          setDetailedTable(data.detailedTable || []);
        } catch (err) {
          console.error('Error fetching charges & collections data:', err);
          setError(err.message);
        } finally {
          setLoading(false);
        }
      };

      fetchData();
    }, 300); // 300ms debounce

    return () => clearTimeout(timer);
  }, [year, month, location, provider, insurance, cpt, filters?.dateType, consolidateLocations]);

  const xAxisKey = month ? 'day' : 'month';

  // Generate period label for KPIs
  const getPeriodLabel = () => {
    if (month) {
      return MONTHS[month - 1]; // Just the month name (e.g., "April")
    } else if (activeDimension) {
      return 'Filtered';
    } else {
      return 'YTD';
    }
  };

  // Export detailed table to CSV
  const exportToCSV = () => {
    // Create CSV headers
    const headers = ['Period', 'Patients', 'Visits', 'Records', 'Charges', 'Payments', 'Adjustments', 'Gross Rate'];

    // Create CSV rows from detailedTable data
    const rows = detailedTable.map(row => [
      row.period,
      row.patients,
      row.visits,
      row.records,
      row.charges,
      row.collections,
      row.adjustments,
      `${row.rate}%`
    ]);

    // Combine headers and rows
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.join(','))
    ].join('\n');

    // Create blob and download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);

    link.setAttribute('href', url);
    link.setAttribute('download', `charges-collections-breakdown-${year}${month ? `-${MONTHS[month - 1]}` : ''}.csv`);
    link.style.visibility = 'hidden';

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '25rem' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">Error loading data: {error}</Alert>
      </Box>
    );
  }

  return (
    <Box>
      {/* Filters */}
      <Card sx={{ boxShadow: 1, mb: 3 }}>
        <CardContent sx={{ p: 2.5 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel sx={{ fontSize: '0.813rem' }}>Year</InputLabel>
                <Select value={year} label="Year" onChange={(e) => setYear(e.target.value)} sx={{ fontSize: '0.813rem' }}>
                  {availableYears.map((yr) => (
                    <MenuItem key={yr} value={yr.toString()} sx={{ fontSize: '0.813rem' }}>
                      {yr}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel sx={{ fontSize: '0.813rem' }}>Month</InputLabel>
                <Select
                  value={month || ''}
                  label="Month"
                  onChange={(e) => setMonth(e.target.value ? parseInt(e.target.value) : null)}
                  sx={{ fontSize: '0.813rem' }}
                >
                  <MenuItem value="" sx={{ fontSize: '0.813rem' }}><em>All Months</em></MenuItem>
                  {MONTHS.map((m, index) => (
                    <MenuItem key={index + 1} value={index + 1} sx={{ fontSize: '0.813rem' }}>{m}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel sx={{ fontSize: '0.813rem' }}>Location</InputLabel>
                <Select
                  value={location}
                  label="Location"
                  onChange={(e) => handleLocationChange(e.target.value)}
                  sx={{
                    fontSize: '0.813rem',
                    '& .MuiSelect-select': {
                      color: activeDimension && activeDimension !== 'location' ? '#94a3b8' : 'inherit',
                    }
                  }}
                >
                  <MenuItem value="All" sx={{ fontSize: '0.813rem' }}>All Locations</MenuItem>
                  {filterOptions.locations.map((loc) => (
                    <MenuItem key={loc} value={loc} sx={{ fontSize: '0.813rem' }}>{loc}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel sx={{ fontSize: '0.813rem' }}>Provider</InputLabel>
                <Select
                  value={provider}
                  label="Provider"
                  onChange={(e) => handleProviderChange(e.target.value)}
                  sx={{
                    fontSize: '0.813rem',
                    '& .MuiSelect-select': {
                      color: activeDimension && activeDimension !== 'provider' ? '#94a3b8' : 'inherit',
                    }
                  }}
                >
                  <MenuItem value="All" sx={{ fontSize: '0.813rem' }}>All Providers</MenuItem>
                  {filterOptions.providers.map((prov) => (
                    <MenuItem key={prov} value={prov} sx={{ fontSize: '0.813rem' }}>{prov}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel sx={{ fontSize: '0.813rem' }}>Payor</InputLabel>
                <Select
                  value={insurance}
                  label="Payor"
                  onChange={(e) => handleInsuranceChange(e.target.value)}
                  sx={{
                    fontSize: '0.813rem',
                    '& .MuiSelect-select': {
                      color: activeDimension && activeDimension !== 'insurance' ? '#94a3b8' : 'inherit',
                    }
                  }}
                >
                  <MenuItem value="All" sx={{ fontSize: '0.813rem' }}>All Payors</MenuItem>
                  {filterOptions.payors.map((payor) => (
                    <MenuItem key={payor} value={payor} sx={{ fontSize: '0.813rem' }}>{payor}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel sx={{ fontSize: '0.813rem' }}>CPT Code</InputLabel>
                <Select
                  value={cpt}
                  label="CPT Code"
                  onChange={(e) => handleCptChange(e.target.value)}
                  sx={{
                    fontSize: '0.813rem',
                    '& .MuiSelect-select': {
                      color: activeDimension && activeDimension !== 'cpt' ? '#94a3b8' : 'inherit',
                    }
                  }}
                >
                  <MenuItem value="All" sx={{ fontSize: '0.813rem' }}>All Codes</MenuItem>
                  {filterOptions.cptCodes.map((code) => (
                    <MenuItem key={code} value={code} sx={{ fontSize: '0.813rem' }}>{code}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Row 1: KPI Cards + Trend Chart */}
      <Grid container spacing={2.5} sx={{ mb: 3 }}>
        {/* Column 1: KPI Cards in Container */}
        <Grid item xs={12} lg={6}>
          <Card sx={{ boxShadow: 1, height: '100%' }}>
            <CardContent sx={{ p: 3, px: 8, display: 'flex', flexDirection: 'column', height: '100%' }}>
              <Box sx={{ mb: 3 }}>
                <Box
                  sx={{
                    bgcolor: '#eff6ff',
                    color: '#2563eb',
                    borderRadius: 2,
                    px: 2,
                    py: 0.75,
                    border: '1px solid #dbeafe',
                    display: 'inline-block',
                  }}
                >
                  <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '0.875rem' }}>
                    Key Metrics
                  </Typography>
                </Box>
              </Box>
              <Grid container spacing={2} sx={{ flex: 1 }}>
                {/* Row 1 */}
                <Grid item xs={6} sx={{ display: 'flex' }}>
                  <Box sx={{
                    p: 2,
                    bgcolor: '#f8fafc',
                    borderRadius: '8px',
                    border: '1px solid #e2e8f0',
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center'
                  }}>
                    <Typography sx={{ fontSize: '0.7rem', color: '#64748b', fontWeight: 500, mb: 0.5 }}>
                      Total Patients
                    </Typography>
                    <Typography sx={{ fontSize: '1.5rem', fontWeight: 700, color: '#1e293b', mb: 0.25 }}>
                      {kpis.totalPatients?.toLocaleString() || 0}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <TrendingUpIcon sx={{ fontSize: '0.75rem', color: '#10b981' }} />
                      <Typography sx={{ fontSize: '0.65rem', color: '#10b981', fontWeight: 600 }}>
                        {getPeriodLabel()}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
                <Grid item xs={6} sx={{ display: 'flex' }}>
                  <Box sx={{
                    p: 2,
                    bgcolor: '#f8fafc',
                    borderRadius: '8px',
                    border: '1px solid #e2e8f0',
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center'
                  }}>
                    <Typography sx={{ fontSize: '0.7rem', color: '#64748b', fontWeight: 500, mb: 0.5 }}>
                      Total Claims
                    </Typography>
                    <Typography sx={{ fontSize: '1.5rem', fontWeight: 700, color: '#1e293b', mb: 0.25 }}>
                      {kpis.totalClaims?.toLocaleString() || 0}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <TrendingUpIcon sx={{ fontSize: '0.75rem', color: '#10b981' }} />
                      <Typography sx={{ fontSize: '0.65rem', color: '#10b981', fontWeight: 600 }}>
                        {getPeriodLabel()}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>

                {/* Row 2 - Empty */}
                <Grid item xs={12} />

                {/* Row 3 */}
                <Grid item xs={6} sx={{ display: 'flex' }}>
                  <Box sx={{
                    p: 2,
                    bgcolor: '#f8fafc',
                    borderRadius: '8px',
                    border: '1px solid #e2e8f0',
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center'
                  }}>
                    <Typography sx={{ fontSize: '0.7rem', color: '#64748b', fontWeight: 500, mb: 0.5 }}>
                      Total Charges
                    </Typography>
                    <Typography sx={{ fontSize: '1.5rem', fontWeight: 700, color: '#1e293b', mb: 0.25 }}>
                      {formatCurrency(kpis.totalCharges)}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <TrendingUpIcon sx={{ fontSize: '0.75rem', color: '#10b981' }} />
                      <Typography sx={{ fontSize: '0.65rem', color: '#10b981', fontWeight: 600 }}>
                        {getPeriodLabel()}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
                <Grid item xs={6} sx={{ display: 'flex' }}>
                  <Box sx={{
                    p: 2,
                    bgcolor: '#f8fafc',
                    borderRadius: '8px',
                    border: '1px solid #e2e8f0',
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center'
                  }}>
                    <Typography sx={{ fontSize: '0.7rem', color: '#64748b', fontWeight: 500, mb: 0.5 }}>
                      Total Payments
                    </Typography>
                    <Typography sx={{ fontSize: '1.5rem', fontWeight: 700, color: '#1e293b', mb: 0.25 }}>
                      {formatCurrency(kpis.totalCollections)}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <TrendingUpIcon sx={{ fontSize: '0.75rem', color: '#10b981' }} />
                      <Typography sx={{ fontSize: '0.65rem', color: '#10b981', fontWeight: 600 }}>
                        {getPeriodLabel()}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Column 2: Charges & Collections Trend Chart */}
        <Grid item xs={12} lg={6}>
          <Card sx={{ boxShadow: 1, height: '100%' }}>
            <CardContent sx={{ p: 2.5 }}>
              <Box sx={{ mb: 2 }}>
                <Box
                  sx={{
                    bgcolor: '#eff6ff',
                    color: '#2563eb',
                    borderRadius: 2,
                    px: 2,
                    py: 0.75,
                    border: '1px solid #dbeafe',
                    display: 'inline-block',
                  }}
                >
                  <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '0.875rem' }}>
                    Trend {month && `- ${MONTHS[month - 1]} ${year}`}
                  </Typography>
                </Box>
              </Box>
              <ResponsiveContainer width="100%" height={300}>
                <ComposedChart data={trendData} barCategoryGap="25%">
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                  <XAxis
                    dataKey={xAxisKey}
                    stroke="#94a3b8"
                    style={{ fontSize: '0.75rem' }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <YAxis
                    stroke="#94a3b8"
                    style={{ fontSize: '0.75rem' }}
                    axisLine={false}
                    tickLine={false}
                    tickFormatter={formatCurrency}
                  />
                  <Tooltip content={<CustomTooltip />} cursor={{ fill: 'transparent' }} />
                  <Legend wrapperStyle={{ fontSize: '0.75rem' }} />
                  <Bar
                    dataKey="charges"
                    fill="#dbeafe"
                    stroke="#2563eb"
                    strokeWidth={1}
                    radius={[4, 4, 0, 0]}
                    name="Charges"
                  />
                  <Line
                    type="monotone"
                    dataKey="collections"
                    stroke="#10b981"
                    strokeWidth={2}
                    dot={{ fill: '#10b981', r: 2 }}
                    name="Payments"
                  />
                </ComposedChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Breakdown Charts - 2x2 Grid */}
      <Grid container spacing={2.5} sx={{ mb: 3 }}>
        {/* By Location */}
        <Grid item xs={12} md={6}>
          <Card sx={{ boxShadow: 1, height: '100%' }}>
            <CardContent sx={{ p: 2.5 }}>
              <Box sx={{ mb: 2 }}>
                <Box
                  sx={{
                    bgcolor: '#eff6ff',
                    color: '#2563eb',
                    borderRadius: 2,
                    px: 2,
                    py: 0.75,
                    border: '1px solid #dbeafe',
                    display: 'inline-block',
                  }}
                >
                  <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '0.875rem' }}>
                    Location View
                  </Typography>
                </Box>
              </Box>
              <Box sx={{ overflowX: 'auto', overflowY: 'hidden' }}>
                <ResponsiveContainer width={Math.max(600, breakdowns.byLocation.length * 50)} height={280}>
                  <ComposedChart data={breakdowns.byLocation} barCategoryGap="35%">
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                    <XAxis
                      dataKey="name"
                      stroke="#94a3b8"
                      style={{ fontSize: '0.625rem' }}
                      axisLine={false}
                      tickLine={false}
                      angle={-45}
                      textAnchor="end"
                      height={100}
                      tickFormatter={toTitleCase}
                    />
                    <YAxis
                      stroke="#94a3b8"
                      style={{ fontSize: '0.75rem' }}
                      axisLine={false}
                      tickLine={false}
                      tickFormatter={formatCurrency}
                    />
                    <Tooltip content={<CustomTooltip />} cursor={{ fill: 'transparent' }} />
                    <Bar dataKey="charges" fill="#dbeafe" stroke="#2563eb" strokeWidth={1} radius={[4, 4, 0, 0]} name="Charges" />
                    <Line type="monotone" dataKey="collections" stroke="#10b981" strokeWidth={2} dot={{ fill: '#10b981', r: 2 }} name="Collections" />
                  </ComposedChart>
                </ResponsiveContainer>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* By Payor */}
        <Grid item xs={12} md={6}>
          <Card sx={{ boxShadow: 1, height: '100%' }}>
            <CardContent sx={{ p: 2.5 }}>
              <Box sx={{ mb: 2 }}>
                <Box
                  sx={{
                    bgcolor: '#eff6ff',
                    color: '#2563eb',
                    borderRadius: 2,
                    px: 2,
                    py: 0.75,
                    border: '1px solid #dbeafe',
                    display: 'inline-block',
                  }}
                >
                  <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '0.875rem' }}>
                    Payor View
                  </Typography>
                </Box>
              </Box>
              <Box sx={{ overflowX: 'auto', overflowY: 'hidden' }}>
                <ResponsiveContainer width={Math.max(600, breakdowns.byPayor.length * 50)} height={280}>
                  <ComposedChart data={breakdowns.byPayor} barCategoryGap="35%">
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                    <XAxis
                      dataKey="name"
                      stroke="#94a3b8"
                      style={{ fontSize: '0.625rem' }}
                      axisLine={false}
                      tickLine={false}
                      angle={-45}
                      textAnchor="end"
                      height={100}
                      tickFormatter={toTitleCase}
                    />
                    <YAxis
                      stroke="#94a3b8"
                      style={{ fontSize: '0.75rem' }}
                      axisLine={false}
                      tickLine={false}
                      tickFormatter={formatCurrency}
                    />
                    <Tooltip content={<CustomTooltip />} cursor={{ fill: 'transparent' }} />
                    <Bar dataKey="charges" fill="#dbeafe" stroke="#2563eb" strokeWidth={1} radius={[4, 4, 0, 0]} name="Charges" />
                    <Line type="monotone" dataKey="collections" stroke="#10b981" strokeWidth={2} dot={{ fill: '#10b981', r: 2 }} name="Collections" />
                  </ComposedChart>
                </ResponsiveContainer>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* By CPT Code */}
        <Grid item xs={12} md={6}>
          <Card sx={{ boxShadow: 1, height: '100%' }}>
            <CardContent sx={{ p: 2.5 }}>
              <Box sx={{ mb: 2 }}>
                <Box
                  sx={{
                    bgcolor: '#eff6ff',
                    color: '#2563eb',
                    borderRadius: 2,
                    px: 2,
                    py: 0.75,
                    border: '1px solid #dbeafe',
                    display: 'inline-block',
                  }}
                >
                  <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '0.875rem' }}>
                    CPT Code View
                  </Typography>
                </Box>
              </Box>
              <Box sx={{ overflowX: 'auto', overflowY: 'hidden' }}>
                <ResponsiveContainer width={Math.max(600, breakdowns.byCpt.length * 50)} height={280}>
                  <ComposedChart data={breakdowns.byCpt} barCategoryGap="35%">
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                    <XAxis
                      dataKey="name"
                      stroke="#94a3b8"
                      style={{ fontSize: '0.625rem' }}
                      axisLine={false}
                      tickLine={false}
                      angle={-45}
                      textAnchor="end"
                      height={100}
                      tickFormatter={toTitleCase}
                    />
                    <YAxis
                      stroke="#94a3b8"
                      style={{ fontSize: '0.75rem' }}
                      axisLine={false}
                      tickLine={false}
                      tickFormatter={formatCurrency}
                    />
                    <Tooltip content={<CustomTooltip />} cursor={{ fill: 'transparent' }} />
                    <Bar dataKey="charges" fill="#dbeafe" stroke="#2563eb" strokeWidth={1} radius={[4, 4, 0, 0]} name="Charges" />
                    <Line type="monotone" dataKey="collections" stroke="#10b981" strokeWidth={2} dot={{ fill: '#10b981', r: 2 }} name="Collections" />
                  </ComposedChart>
                </ResponsiveContainer>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* By Provider */}
        <Grid item xs={12} md={6}>
          <Card sx={{ boxShadow: 1, height: '100%' }}>
            <CardContent sx={{ p: 2.5 }}>
              <Box sx={{ mb: 2 }}>
                <Box
                  sx={{
                    bgcolor: '#eff6ff',
                    color: '#2563eb',
                    borderRadius: 2,
                    px: 2,
                    py: 0.75,
                    border: '1px solid #dbeafe',
                    display: 'inline-block',
                  }}
                >
                  <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '0.875rem' }}>
                    Provider View
                  </Typography>
                </Box>
              </Box>
              <Box sx={{ overflowX: 'auto', overflowY: 'hidden' }}>
                <ResponsiveContainer width={Math.max(600, breakdowns.byProvider.length * 50)} height={280}>
                  <ComposedChart data={breakdowns.byProvider} barCategoryGap="35%">
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                    <XAxis
                      dataKey="name"
                      stroke="#94a3b8"
                      style={{ fontSize: '0.625rem' }}
                      axisLine={false}
                      tickLine={false}
                      angle={-45}
                      textAnchor="end"
                      height={100}
                      tickFormatter={toTitleCase}
                    />
                    <YAxis
                      stroke="#94a3b8"
                      style={{ fontSize: '0.75rem' }}
                      axisLine={false}
                      tickLine={false}
                      tickFormatter={formatCurrency}
                    />
                    <Tooltip content={<CustomTooltip />} cursor={{ fill: 'transparent' }} />
                    <Bar dataKey="charges" fill="#dbeafe" stroke="#2563eb" strokeWidth={1} radius={[4, 4, 0, 0]} name="Charges" />
                    <Line type="monotone" dataKey="collections" stroke="#10b981" strokeWidth={2} dot={{ fill: '#10b981', r: 2 }} name="Collections" />
                  </ComposedChart>
                </ResponsiveContainer>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Detailed Table */}
      <Card sx={{ boxShadow: 1 }}>
        <CardContent sx={{ p: 2.5 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Box
              sx={{
                bgcolor: '#eff6ff',
                color: '#2563eb',
                borderRadius: 2,
                px: 2,
                py: 0.75,
                border: '1px solid #dbeafe',
                display: 'inline-block',
              }}
            >
              <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '0.875rem' }}>
                Detailed Monthly Breakdown
              </Typography>
            </Box>
            <Button
              startIcon={<DownloadIcon />}
              variant="outlined"
              size="small"
              onClick={exportToCSV}
              sx={{ fontSize: '0.75rem', textTransform: 'none' }}
            >
              Export
            </Button>
          </Box>
          <TableContainer component={Paper} sx={{ maxHeight: 400, boxShadow: 0 }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ bgcolor: '#f8fafc', fontWeight: 600, fontSize: '0.75rem' }}>Period</TableCell>
                  <TableCell align="right" sx={{ bgcolor: '#f8fafc', fontWeight: 600, fontSize: '0.75rem' }}>Patients</TableCell>
                  <TableCell align="right" sx={{ bgcolor: '#f8fafc', fontWeight: 600, fontSize: '0.75rem' }}>Visits</TableCell>
                  <TableCell align="right" sx={{ bgcolor: '#f8fafc', fontWeight: 600, fontSize: '0.75rem' }}>Records</TableCell>
                  <TableCell align="right" sx={{ bgcolor: '#f8fafc', fontWeight: 600, fontSize: '0.75rem' }}>Charges</TableCell>
                  <TableCell align="right" sx={{ bgcolor: '#f8fafc', fontWeight: 600, fontSize: '0.75rem' }}>Payments</TableCell>
                  <TableCell align="right" sx={{ bgcolor: '#f8fafc', fontWeight: 600, fontSize: '0.75rem' }}>Adjustments</TableCell>
                  <TableCell align="right" sx={{ bgcolor: '#f8fafc', fontWeight: 600, fontSize: '0.75rem' }}>Gross Rate</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {detailedTable.map((row, index) => (
                  <TableRow key={index} hover sx={{ '&:hover': { bgcolor: '#f8fafc' } }}>
                    <TableCell sx={{ fontSize: '0.75rem' }}>{row.period}</TableCell>
                    <TableCell align="right" sx={{ fontSize: '0.75rem' }}>{row.patients?.toLocaleString()}</TableCell>
                    <TableCell align="right" sx={{ fontSize: '0.75rem' }}>{row.visits?.toLocaleString()}</TableCell>
                    <TableCell align="right" sx={{ fontSize: '0.75rem' }}>{row.records?.toLocaleString()}</TableCell>
                    <TableCell align="right" sx={{ fontSize: '0.75rem' }}>${row.charges?.toLocaleString()}</TableCell>
                    <TableCell align="right" sx={{ fontSize: '0.75rem', color: '#10b981' }}>${row.collections?.toLocaleString()}</TableCell>
                    <TableCell align="right" sx={{ fontSize: '0.75rem', color: '#ef4444' }}>${row.adjustments?.toLocaleString()}</TableCell>
                    <TableCell align="right" sx={{ fontSize: '0.75rem', color: '#10b981', fontWeight: 600 }}>{row.rate}%</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
    </Box>
  );
}

export default ChargesAndCollections;
